import { useCallback, useMemo } from 'react';
import { useFinanceApi } from 'infrastructure/api';
import { CrudService, ResourceData, useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import { useSingletonPromise } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';
import { CONTENT_TYPE } from 'framework/api';
import { CREATE_WASTE_WITH_VIOLATIONS, WASTE_REPORTING_METHOD, WASTE_TYPE } from './model';
import useWasteValidation from './useWasteValidation';
import { WASTE_RESOURCE_URL } from './route/route';

type Waste = {
  type: keyof typeof WASTE_TYPE;
  laborWasteDetails?: {
    employeeAlias?: string;
    reportingMethod: keyof typeof WASTE_REPORTING_METHOD;
    period?: any;
    projectId?: string;
    ticketsIds?: string[];
    inputTicketsIds?: string;
  };
  otherWasteDetails?: {
    cost: {
      amount: string;
      currency: string;
    };
    issueDate: string;
  };
};

const useWasteService = () => {
  const { sendGetRequest, sendPostRequest } = useFinanceApi();
  const { isGranted } = useAuthorization();

  const {
    search: baseSearch,
    create: initialCreate,
    ...baseCrud
  }: CrudService = useCrudService({
    singleResourceUrl: `${WASTE_RESOURCE_URL}/:id`,
    listResourceUrl: WASTE_RESOURCE_URL,
    useService: useFinanceApi,
  });

  const isHasWithViolations = useMemo(() => isGranted(CREATE_WASTE_WITH_VIOLATIONS), [isGranted]);

  const search = useCallback(
    (request: ResourceData) => {
      const searchParams = cloneDeep(request);
      const searchString = searchParams.filter?.['specification:search']?.eq || '';

      if (searchString && searchParams.filter) {
        delete searchParams.filter?.['specification:search'];
        searchParams.filter.name = {
          ct: searchString,
        };
      }

      return baseSearch(searchParams);
    },
    [baseSearch],
  );

  const create = useCallback(
    async (waste: Waste) => {
      const mappedData: Waste = { ...waste };

      if (mappedData.type === WASTE_TYPE.LABOR_COST_WASTE) {
        const reportingMethod = mappedData.laborWasteDetails?.reportingMethod;

        delete mappedData.otherWasteDetails;

        if (reportingMethod === WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS) {
          delete mappedData?.laborWasteDetails?.period;
          delete mappedData?.laborWasteDetails?.projectId;
        }

        if (reportingMethod === WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD) {
          delete mappedData?.laborWasteDetails?.ticketsIds;
        }

        if (reportingMethod === WASTE_REPORTING_METHOD.BY_TICKET_IDS) {
          delete mappedData?.laborWasteDetails?.period;
          delete mappedData?.laborWasteDetails?.projectId;
          delete mappedData?.laborWasteDetails?.employeeAlias;
        }
      }

      if (mappedData.type === WASTE_TYPE.OTHER_EXPENSES_WASTE) {
        delete mappedData.laborWasteDetails;
      }

      return initialCreate(mappedData);
    },
    [initialCreate],
  );

  const getAccounts = useSingletonPromise(
    'search-waste-accounts',
    useCallback(async () => {
      const response = await sendGetRequest('/accounting-cycle/waste/accounts');
      const result = await response.json();

      return {
        result,
        totalElements: result.length,
        totalPages: 1,
      };
    }, [sendGetRequest]),
  );

  const getRedmineTickets = useSingletonPromise(
    'search-waste-redmine-tickets',
    useCallback(
      async (data: any) => {
        const response = await sendPostRequest('/accounting-cycle/waste/tickets', data);
        const result = await response.json();

        return {
          result,
          totalElements: result.length,
          totalPages: 1,
        };
      },
      [sendPostRequest],
    ),
  );

  const getProjects = useSingletonPromise(
    'search-waste-redmine-projects',
    useCallback(
      async (data: any) => {
        const response = await sendPostRequest('/accounting-cycle/waste/projects', data);
        const result = await response.json();

        return {
          result,
          totalElements: result.length,
          totalPages: 1,
        };
      },
      [sendPostRequest],
    ),
  );

  const approveWaste = useCallback(
    async (id: string, data: any) => {
      const result = await sendPostRequest(`${WASTE_RESOURCE_URL}/${id}/approve`, data);
      return result.json();
    },
    [sendPostRequest],
  );

  const rejectWaste = useCallback(
    async (id: string, rejectReason: any) => {
      const result = await sendPostRequest(`${WASTE_RESOURCE_URL}/${id}/reject`, rejectReason, {
        contentType: CONTENT_TYPE.MULTIPART,
      });
      return result.json();
    },
    [sendPostRequest],
  );

  const wasteValidation = useWasteValidation(isHasWithViolations);

  const getValidationSchema = useCallback(() => Promise.resolve(wasteValidation), [wasteValidation]);

  return {
    ...baseCrud,
    create,
    search,
    getAccounts,
    getRedmineTickets,
    approveWaste,
    rejectWaste,
    getProjects,
    getValidationSchema,
    isHasWithViolations,
  };
};

export default useWasteService;
